import { logger } from '@/logging';
import { User } from '@/types';
import {
  AccountInfo,
  InteractionRequiredAuthError,
  SilentRequest,
} from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useCallback, useMemo } from 'react';
import { isAuthBypassed, loginRequest, tokenRequest } from './config';
import { useSimpleAuth } from './SimpleAuth';

/**
 * Converts MSAL AccountInfo to our User type
 */
function accountToUser(account: AccountInfo | null): User | null {
  if (!account) return null;

  // Extract roles from ID token claims
  const idTokenClaims = account.idTokenClaims as
    | { roles?: string[] }
    | undefined;
  const roles = idTokenClaims?.roles || [];

  return {
    id: account.homeAccountId,
    email: account.username || '',
    name: account.name || '',
    roles,
  };
}


/**
 * Mock user for simple auth mode
 */
const SIMPLE_AUTH_USER: User = {
  id: 'cortex-admin-001',
  email: 'admin@cortexflow.com',
  name: 'CortexFlow Administrator',
  roles: ['admin'], // Give full access in simple auth mode
};

/**
 * Custom hook for authentication operations
 */
export function useAuth() {
  // Handle simple auth vs MSAL auth
  let instance: any = null, accounts: any[] = [], inProgress: any = 'none', isAuthenticated: boolean = false;
  let simpleAuth = null;

  if (isAuthBypassed) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    simpleAuth = useSimpleAuth();
    isAuthenticated = simpleAuth.isAuthenticated;
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    ({ instance, accounts, inProgress } = useMsal());
    // eslint-disable-next-line react-hooks/rules-of-hooks
    isAuthenticated = useIsAuthenticated();
  }

  // Get the active account
  const account = useMemo(() => {
    if (isAuthBypassed) return null;
    return instance?.getActiveAccount() || accounts[0] || null;
  }, [instance, accounts]);

  // Convert to User type
  const user = useMemo(() => {
    if (isAuthBypassed) return SIMPLE_AUTH_USER;
    return accountToUser(account);
  }, [account]);

  /**
   * Login with popup
   */
  const login = useCallback(async () => {
    if (isAuthBypassed) {
      logger.debug('Auth hooks: Mock login');
      return;
    }
    try {
      const response = await instance?.loginPopup(loginRequest);
      if (response?.account && instance) {
        instance.setActiveAccount(response.account);
      }
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [instance]);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    if (isAuthBypassed) {
      if (simpleAuth) {
        simpleAuth.logout();
      }
      return;
    }
    try {
      await instance?.logout({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }, [instance, simpleAuth]);

  /**
   * Get access token for API calls
   */
  const getAccessToken = useCallback(async (): Promise<string> => {
    if (isAuthBypassed) {
      return 'mock-access-token';
    }

    if (!account) {
      throw new Error('No authenticated user');
    }

    const request: SilentRequest = {
      ...tokenRequest,
      account,
      // Always use forceRefresh from tokenRequest config
      forceRefresh: tokenRequest.forceRefresh,
    };

    try {
      // Try to acquire token silently
      const response = await instance?.acquireTokenSilent(request);
      // Return ID token for SSO validation (not access token for API calls)
      // The ID token proves the user's identity, which is what we need for SSO + allowlist
      return response?.idToken || response?.accessToken || '';
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Fallback to interactive method
        try {
          const response = await instance?.acquireTokenPopup(request);
          // Return ID token for SSO validation
          return response?.idToken || response?.accessToken || '';
        } catch (popupError) {
          console.error('Failed to acquire token interactively:', popupError);
          throw popupError;
        }
      }
      throw error;
    }
  }, [instance, account]);

  /**
   * Check if user has specific role(s)
   */
  const hasRole = useCallback(
    (requiredRoles: string | string[]): boolean => {
      if (!user) return false;

      const roles = Array.isArray(requiredRoles)
        ? requiredRoles
        : [requiredRoles];
      return roles.some(role => user.roles.includes(role));
    },
    [user]
  );

  return {
    user,
    isAuthenticated: isAuthBypassed ? true : isAuthenticated,
    isLoading: isAuthBypassed ? false : inProgress !== 'none',
    login,
    logout,
    getAccessToken,
    hasRole,
  };
}
